import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Save, Trash2, Sparkles, MessageSquarePlus, Gauge, Lock } from "lucide-react";
import ProposalPlan from "../../components/smartreach/ProposalPlan";
import ProposalDeliverables from "../../components/smartreach/ProposalDeliverables";
import { sr } from "../../lib/sr";

const STAGES = ["draft", "ready_for_review", "shared", "negotiating", "accepted", "declined", "expired"];
const STAGE_LABEL = { draft: "Draft", ready_for_review: "Ready for review", shared: "Shared", negotiating: "Negotiating", accepted: "Accepted", declined: "Declined", expired: "Expired" };
const LOST_REASONS = [
  { v: "price_too_high", l: "Price too high" }, { v: "timing", l: "Timing" },
  { v: "chose_competitor", l: "Chose a competitor" }, { v: "no_budget", l: "No budget" },
  { v: "no_response", l: "No response" }, { v: "scope_mismatch", l: "Scope mismatch" }, { v: "other", l: "Other" },
];
const LOST_LABEL = Object.fromEntries(LOST_REASONS.map((r) => [r.v, r.l]));
const fieldStyle = { width: "100%", padding: "9px 11px", border: "1px solid var(--border)", borderRadius: 8, fontSize: 13.5, background: "#fff" };

function DiscoveryField({ q, value, onChange }) {
  const common = { style: fieldStyle, value: value ?? "", onChange: (e) => onChange(e.target.value), "data-testid": `disc-${q.key}` };
  if (q.type === "textarea") return <textarea {...common} rows={3} placeholder={q.placeholder} />;
  if (q.type === "bool") return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`disc-${q.key}`} /> Yes
    </label>
  );
  if (q.type === "select") return (
    <select {...common}><option value="">—</option>{(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
  );
  if (q.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid={`disc-${q.key}`}>
        {(q.options || []).map((o) => (
          <button key={o} type="button" onClick={() => toggle(o)} className={`sr-btn sr-btn-sm ${arr.includes(o) ? "sr-btn-navy" : "sr-btn-ghost"}`}>{o}</button>
        ))}
      </div>
    );
  }
  const type = q.type === "number" || q.type === "currency" ? "number" : "text";
  return <input {...common} type={type} placeholder={q.placeholder} />;
}

function Card({ title, children, right }) {
  return (
    <div className="sr-card" style={{ padding: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>{right}
      </div>
      {children}
    </div>
  );
}

const norm = (v) => (v === null || v === undefined ? "" : String(v)).trim();

// Shows the AI Growth Plan suggestion for a field when it differs from the salesperson's current value.
function AiSuggestChip({ aiVal, current, onUse, fmt }) {
  if (aiVal === null || aiVal === undefined || aiVal === "") return null;
  if (norm(aiVal) === norm(current)) return null;
  const shown = fmt ? fmt(aiVal) : String(aiVal);
  return (
    <div data-testid="ai-suggest-chip" style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 8, padding: "6px 9px" }}>
      <Sparkles size={13} style={{ color: "#7C3AED", flexShrink: 0 }} />
      <span style={{ fontSize: 11.5, color: "#5B21B6", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={shown}>
        AI plan: <strong>{shown}</strong>
      </span>
      <button type="button" className="sr-btn sr-btn-sm" onClick={onUse} data-testid="ai-use-field"
        style={{ background: "#7C3AED", color: "#fff", padding: "3px 10px", fontSize: 11, borderRadius: 6, flexShrink: 0 }}>Use this</button>
    </div>
  );
}

function AiBasisBanner({ ai, src, onApplyAll, hasPending }) {
  if (!src) return null;
  if (!ai || !ai.has_plan) {
    return (
      <div data-testid="ai-basis-banner" className="sr-card" style={{ padding: "12px 16px", marginTop: 16, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#92400E" }}>
          <Sparkles size={15} />
          <span>No AI Growth Plan yet for this client. The proposal uses smart defaults — build a plan in the Add Client journey to base the proposal on it.</span>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="ai-basis-banner" className="sr-card" style={{ padding: "14px 16px", marginTop: 16, background: "linear-gradient(90deg,#F5F3FF,#FFFFFF)", border: "1px solid #DDD6FE" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Sparkles size={16} style={{ color: "#7C3AED" }} />
            <strong style={{ fontSize: 13.5, color: "#5B21B6" }}>Based on the AI Growth Plan (v{ai.plan_version})</strong>
            <span className="sr-badge" style={{ fontSize: 10, background: "#EDE9FE", color: "#6D28D9" }}>{src.onboarding_completed ? "Add Client ✓" : "Add Client pending"}</span>
          </div>
          {ai.summary && <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--muted)", lineHeight: 1.5 }}>{ai.summary}</p>}
          {ai.audience_strategy && <p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)" }}><strong>Audiences:</strong> {ai.audience_strategy}</p>}
        </div>
        <button type="button" className="sr-btn sr-btn-sm" onClick={onApplyAll} disabled={!hasPending} data-testid="ai-apply-all"
          style={{ background: hasPending ? "#7C3AED" : "#C4B5FD", color: "#fff", flexShrink: 0 }}>
          <Sparkles size={13} /> {hasPending ? "Apply all AI suggestions" : "All AI values applied"}
        </button>
      </div>
    </div>
  );
}

export default function ProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [schema, setSchema] = useState([]);
  const [draft, setDraft] = useState({});
  const [source, setSource] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rec, setRec] = useState(null);
  const [fb, setFb] = useState({ note: "", sentiment: "positive" });
  const [decline, setDecline] = useState(null);

  const hydrate = (prop, sch, src) => {
    setP(prop);
    if (sch) setSchema(sch);
    if (src !== undefined) setSource(src);
    const d = prop.draft || {};
    setDraft({
      objective: d.objective || "", promote: d.promote || "", geography: d.geography || "",
      budget: d.budget ?? "", timing: (d.timing || {}).approx || "",
      contact: { name: (d.contact || {}).name || "", email: (d.contact || {}).email || "", phone: (d.contact || {}).phone || "" },
      discovery: { ...(d.discovery || {}) },
    });
  };
  const reload = () => sr.getProposal(id).then((r) => hydrate(r.proposal, r.discovery_schema, r.source)).catch(() => {});
  useEffect(() => {
    sr.getProposal(id).then((r) => hydrate(r.proposal, r.discovery_schema, r.source)).catch((e) => { toast.error(e?.response?.data?.detail || "Couldn't load proposal"); nav("/app/proposals"); });
    // eslint-disable-next-line
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await sr.updateProposal(id, {
        objective: draft.objective, promote: draft.promote, geography: draft.geography,
        budget: draft.budget === "" ? null : parseFloat(draft.budget),
        timing: { approx: draft.timing }, contact: draft.contact, discovery: draft.discovery,
      });
      hydrate(r.proposal);
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };
  const changeStage = async (stage) => {
    if (stage === "declined") { setDecline({ reason: "no_response", note: "" }); return; }
    try { const r = await sr.proposalStage(id, stage); setP((cur) => ({ ...cur, ...r.proposal })); toast.success(`Stage → ${STAGE_LABEL[stage]}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change stage"); }
  };
  const confirmDecline = async () => {
    try {
      const r = await sr.proposalStage(id, "declined", { lost_reason: decline.reason, lost_note: decline.note.trim() || null });
      setP((cur) => ({ ...cur, ...r.proposal })); setDecline(null); toast.success("Marked as declined");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't decline"); }
  };
  const recommend = async () => {
    try { const r = await sr.recommendProposalBudget(id); setRec(r.recommendation); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't recommend"); }
  };
  const addFeedback = async () => {
    if (!fb.note.trim()) return;
    try { const r = await sr.proposalFeedback(id, fb.note.trim(), fb.sentiment); setP((cur) => ({ ...cur, feedback: r.proposal.feedback })); setFb({ note: "", sentiment: "positive" }); toast.success("Feedback recorded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add feedback"); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this prospect and proposal? This cannot be undone.")) return;
    try { await sr.deleteProposal(id); toast.success("Deleted"); nav("/app/proposals"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete"); }
  };

  if (!p) return <div style={{ textAlign: "center", padding: 60 }}><Loader2 className="sr-spin" size={26} /></div>;
  const pros = p.prospect || {};
  const up = (k, v) => setDraft((s) => ({ ...s, [k]: v }));
  const upContact = (k, v) => setDraft((s) => ({ ...s, contact: { ...s.contact, [k]: v } }));
  const upDisc = (k, v) => setDraft((s) => ({ ...s, discovery: { ...s.discovery, [k]: v } }));
  const ai = source?.ai || null;
  const aiDiffers = (k) => ai && ai[k] !== null && ai[k] !== undefined && ai[k] !== "" && norm(ai[k]) !== norm(draft[k]);
  const aiPending = ai?.has_plan && ["objective", "promote", "geography", "budget"].some(aiDiffers);
  const applyAllAi = () => {
    if (!ai) return;
    setDraft((s) => {
      const n = { ...s };
      ["objective", "promote", "geography"].forEach((k) => { if (ai[k]) n[k] = ai[k]; });
      if (ai.budget) n.budget = ai.budget;
      return n;
    });
    toast.success("Applied the AI Growth Plan recommendation — review, then Save");
  };

  return (
    <div data-testid="proposal-detail-page" style={{ padding: "20px 26px", maxWidth: 1000, margin: "0 auto" }}>
      <button onClick={() => nav("/app/proposals")} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="back-to-proposals" style={{ marginBottom: 12 }}><ArrowLeft size={14} /> All proposals</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>{pros.name}</h1>
          <p className="sr-muted" style={{ marginTop: 3, fontSize: 12.5 }}>{pros.industry_label} · {pros.website || "no website"} · <span style={{ color: "var(--gold)" }}>Proposal (prospect — not billed)</span></p>
          {(p.stage === "declined" || p.stage === "expired") && p.lost_reason ? (
            <p data-testid="lost-reason-badge" style={{ marginTop: 4, fontSize: 12, color: "#B42318", fontWeight: 600 }}>
              {p.stage === "expired" ? "Expired" : "Lost"}: {LOST_LABEL[p.lost_reason] || p.lost_reason}{p.lost_note ? ` — ${p.lost_note}` : ""}
            </p>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={p.stage} onChange={(e) => changeStage(e.target.value)} style={{ ...fieldStyle, width: "auto" }} data-testid="proposal-stage-select">
            {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
          </select>
          <button onClick={remove} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="delete-proposal" title="Delete prospect"><Trash2 size={15} /></button>
        </div>
      </div>

      <AiBasisBanner ai={ai} src={source} onApplyAll={applyAllAi} hasPending={aiPending} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 16 }}>
        <Card title="Campaign brief">
          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Objective<input style={fieldStyle} value={draft.objective} onChange={(e) => up("objective", e.target.value)} data-testid="pd-objective" />
              <AiSuggestChip aiVal={ai?.objective} current={draft.objective} onUse={() => up("objective", ai.objective)} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>What to promote<input style={fieldStyle} value={draft.promote} onChange={(e) => up("promote", e.target.value)} data-testid="pd-promote" />
              <AiSuggestChip aiVal={ai?.promote} current={draft.promote} onUse={() => up("promote", ai.promote)} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Target geography<input style={fieldStyle} value={draft.geography} onChange={(e) => up("geography", e.target.value)} data-testid="pd-geography" />
              <AiSuggestChip aiVal={ai?.geography} current={draft.geography} onUse={() => up("geography", ai.geography)} />
            </label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Timing<input style={fieldStyle} value={draft.timing} onChange={(e) => up("timing", e.target.value)} data-testid="pd-timing" /></label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Monthly budget
              <div style={{ display: "flex", gap: 8 }}>
                <input style={fieldStyle} type="number" value={draft.budget} onChange={(e) => up("budget", e.target.value)} data-testid="pd-budget" placeholder="Not set" />
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={recommend} data-testid="pd-recommend-budget"><Gauge size={14} /> Recommend</button>
              </div>
              <AiSuggestChip aiVal={ai?.budget} current={draft.budget} onUse={() => up("budget", ai.budget)} fmt={(v) => `$${Number(v).toLocaleString()}/mo`} />
            </label>
            {rec && (
              <div style={{ background: "#f8fafc", border: "1px solid var(--border)", borderRadius: 8, padding: 10, fontSize: 12 }} data-testid="pd-budget-rec">
                <strong>${rec.recommended_monthly.toLocaleString()}/mo</strong> <span className="sr-muted">(range ${rec.range.low.toLocaleString()}–${rec.range.high.toLocaleString()}; estimate)</span>
                <div className="sr-muted" style={{ marginTop: 4 }}>{rec.method}. {rec.assumptions.join(" ")}</div>
                <button className="sr-btn sr-btn-sm sr-btn-navy" style={{ marginTop: 6 }} onClick={() => { up("budget", rec.recommended_monthly); setRec(null); }} data-testid="pd-apply-budget">Apply ${rec.recommended_monthly.toLocaleString()}</button>
              </div>
            )}
          </div>
        </Card>

        <Card title="Prospect contact">
          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Contact name<input style={fieldStyle} value={draft.contact.name} onChange={(e) => upContact("name", e.target.value)} data-testid="pd-contact-name" /></label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Email<input style={fieldStyle} value={draft.contact.email} onChange={(e) => upContact("email", e.target.value)} data-testid="pd-contact-email" /></label>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Phone<input style={fieldStyle} value={draft.contact.phone} onChange={(e) => upContact("phone", e.target.value)} data-testid="pd-contact-phone" /></label>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card title={`${pros.industry_label || "Industry"} discovery`}>
          {schema.length === 0 ? <p className="sr-muted" style={{ fontSize: 12.5 }}>No extra discovery questions for this industry.</p> : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {schema.map((q) => (
                <label key={q.key} style={{ fontSize: 12, fontWeight: 600, gridColumn: q.type === "textarea" || q.type === "multiselect" ? "1 / -1" : "auto" }}>
                  {q.label}
                  <div style={{ marginTop: 4 }}><DiscoveryField q={q} value={draft.discovery[q.key]} onChange={(v) => upDisc(q.key, v)} /></div>
                </label>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
        <button className="sr-btn sr-btn-gold" onClick={save} disabled={saving} data-testid="pd-save">{saving ? <Loader2 size={15} className="sr-spin" /> : <Save size={15} />} Save changes</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
        <Card title="Prospect feedback">
          <div style={{ display: "grid", gap: 8 }}>
            {(p.feedback || []).length === 0 && <p className="sr-muted" style={{ fontSize: 12.5 }}>No feedback recorded yet.</p>}
            {(p.feedback || []).map((f, i) => (
              <div key={i} style={{ fontSize: 12.5, borderLeft: "3px solid var(--border)", paddingLeft: 10 }} data-testid={`feedback-item-${i}`}>
                <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{f.sentiment || "note"}</span> — {f.note}
                <div className="sr-muted" style={{ fontSize: 10.5 }}>{new Date(f.at).toLocaleString()}</div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <select value={fb.sentiment} onChange={(e) => setFb({ ...fb, sentiment: e.target.value })} style={{ ...fieldStyle, width: 110 }} data-testid="fb-sentiment">
                <option value="positive">Positive</option><option value="neutral">Neutral</option><option value="negative">Negative</option>
              </select>
              <input style={fieldStyle} value={fb.note} onChange={(e) => setFb({ ...fb, note: e.target.value })} placeholder="Add prospect feedback…" data-testid="fb-note" />
              <button className="sr-btn sr-btn-navy sr-btn-sm" onClick={addFeedback} data-testid="fb-add"><MessageSquarePlus size={14} /></button>
            </div>
          </div>
        </Card>

        <ProposalPlan pid={id} draft={p.draft || {}} ai={ai} />
      </div>

      <div style={{ marginTop: 14 }}>
        <ProposalDeliverables pid={id} proposal={p} onConverted={reload} />
      </div>

      {decline ? (
        <div data-testid="decline-modal" onClick={() => setDecline(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(10,18,38,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 22, width: 420, maxWidth: "100%" }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 17 }}>Mark proposal as declined</h3>
            <p className="sr-muted" style={{ marginTop: 0, fontSize: 12.5 }}>Capture why so it shows up in your analytics.</p>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Reason
              <select value={decline.reason} onChange={(e) => setDecline({ ...decline, reason: e.target.value })} style={{ ...fieldStyle, marginTop: 4 }} data-testid="decline-reason">
                {LOST_REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginTop: 10 }}>Note (optional)
              <input style={{ ...fieldStyle, marginTop: 4 }} value={decline.note} onChange={(e) => setDecline({ ...decline, note: e.target.value })} placeholder="Any context…" data-testid="decline-note" />
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setDecline(null)} data-testid="decline-cancel">Cancel</button>
              <button className="sr-btn sr-btn-navy sr-btn-sm" onClick={confirmDecline} data-testid="decline-confirm">Mark declined</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
